<template>
  <div :class="['base-form', `base-form-${formName}`]">
    <VForm ref="form" :errors="errors" :model="form">
      <VFormItem
        v-if="fields.includes('first_name')"
        :errors="errors"
        :prop="'data.first_name'"
        class="base-form--first_name"
      >
        <VInput v-model="form.data.first_name" :label="labels.first_name" :placeholder="placeholders.first_name" />
      </VFormItem>
      <VFormItem
        v-if="fields.includes('last_name')"
        :errors="errors"
        :prop="'data.last_name'"
        class="base-form--last_name"
      >
        <VInput v-model="form.data.last_name" :label="labels.last_name" :placeholder="placeholders.last_name" />
      </VFormItem>
      <VFormItem v-if="fields.includes('phone')" :errors="errors" :prop="'data.phone'" class="base-form--phone">
        <VInput
          v-model="form.data.phone"
          :label="labels.phone"
          :mask="convertedMask"
          :placeholder="placeholders.phone"
          type="tel"
          autocomplete="tel"
        />
      </VFormItem>
      <VFormItem v-if="fields.includes('email')" :errors="errors" :prop="'data.email'" class="base-form--email">
        <VInput v-model="form.data.email" :label="labels.email" :placeholder="placeholders.email" type="email" />
      </VFormItem>
      <VFormItem
        v-if="fields.includes('category_id')"
        :errors="errors"
        :prop="'data.category_id'"
        class="base-form--category_id"
      >
        <VSelect
          v-model="form.data.category_id"
          :label="'title'"
          :label_inp="labels.cat"
          :max-height="'100px'"
          :options="directory.categories"
          :placeholder="placeholders.cat"
          :reduce="(cat) => cat.id"
        >
          <template #no-options>
            <div />
          </template>
        </VSelect>
      </VFormItem>
      <VFormItem
        v-if="fields.includes('region_id')"
        :errors="errors"
        :prop="'data.region_id'"
        class="base-form--region_id"
      >
        <VSelect
          v-model="form.data.region_id"
          :label="'title'"
          :label_inp="labels.city"
          :max-height="'100px'"
          :options="cities"
          :placeholder="placeholders.city"
          :reduce="(city) => city.id"
          :search-fn="getCities"
        >
          <template #no-options>
            <div>
              <p>{{ labels.cityNoOptions }}</p>
            </div>
          </template>
        </VSelect>
      </VFormItem>
      <VFormItem v-if="fields.includes('message')" :errors="errors" :prop="'data.message'" class="base-form--message">
        <VTextarea v-model="form.data.message" :label="labels.message" rows="4" />
      </VFormItem>
      <VFormItem v-if="fields.includes('company')" :errors="errors" :prop="'data.company'" class="base-form--company">
        <VInput v-model="form.data.company" :label="labels.company" :placeholder="placeholders.company" />
      </VFormItem>
      <VFormItem v-if="fields.includes('role_id')" :errors="errors" :prop="'data.role_id'" class="base-form--role_id">
        <VSelect
          v-model="form.data.role_id"
          :label="'title'"
          :label_inp="labels.role"
          :max-height="'100px'"
          :options="directory.roles"
          :placeholder="placeholders.role"
          :reduce="(role) => role.id"
        >
          <template #no-options>
            <div />
          </template>
        </VSelect>
      </VFormItem>
    </VForm>

    <VFormErrorsUnlinked :errors="errors" :form-children="$refs.form ? $refs.form.$children : []" />

    <div v-if="$slots.agree" class="agree">
      <VCheckbox :id="agreeId" v-model="agree" :error="!agree" />
      <label :for="agreeId" class="sub-text">
        <slot name="agree" />
      </label>
    </div>

    <div class="base-form--submit-button">
      <button :disabled="disableSend" class="button button-primary" @click="sendForm">
        {{ labels.buttonSubmit }}
      </button>
    </div>
    <p class="sub-text">
      {{ labels.requireText }}
    </p>
  </div>
</template>

<script>
import VForm from './Form/VForm.vue';
import VFormErrorsUnlinked from './Form/VFormErrorsUnlinked.vue';
import VFormItem from './Form/VFormItem.vue';
import VSelect from './Form/VSelect.vue';
import VInput from './Form/VInput.vue';
import VTextarea from './Form/VTextarea.vue';
import VCheckbox from './Form/VCheckbox.vue';
import sendDebounceMixin from 'COMMON_COMPONENTS/Form/mixins/send-debounce-mixin';
import {uuid} from 'COMMON_UTILITY/utils';
import {requestBid, request} from 'COMMON_UTILITY/request';

const AVAILABLE_FIELDS = [
  'first_name',
  'last_name',
  'email',
  'phone',
  'category_id',
  'region_id',
  'message',
  'company',
  'role_id',
];

export default {
  name: 'BaseForm',
  components: {
    VForm,
    VFormErrorsUnlinked,
    VFormItem,
    VInput,
    VCheckbox,
    VSelect,
    VTextarea,
  },
  mixins: [sendDebounceMixin],
  props: {
    // Справочники для селектов
    directory: {
      type: Object,
      default() {
        return {};
      },
    },
    // Дополнительные поля, которые надо отправить вместе с формой
    extraData: {
      type: Object,
      default() {
        return {};
      },
    },
    // Список полей из набора, которые показывать в форме
    fields: {
      type: Array,
      required: true,
      validator(fields) {
        return fields.every((f) => AVAILABLE_FIELDS.includes(f));
      },
    },
    // Определяет метод в API, куда отправляются данные
    formName: {type: String, required: true},
    // Событие GTM после успешной отправки формы
    gtmEvent: {type: String, default: ''},
    // Маска номера телефона
    mask: {type: String, default: ''},
    // Текстовые фрагменты
    labels: {
      type: Object,
      default() {
        return {};
      },
    },
    // Плейсхолдеры для инпутов
    placeholders: {
      type: Object,
      default() {
        return {};
      },
    },
  },
  data() {
    let data = {};
    AVAILABLE_FIELDS.forEach((field) => {
      data[field] = '';
    });
    return {
      agreeId: uuid(),
      agree: false,
      errors: {},
      cities: [],
      form: {data},
    };
  },
  computed: {
    hasSlotAgree() {
      return !!this.$slots.agree;
    },
    disableSend() {
      return (this.hasSlotAgree && !this.agree) || !this.sendAllowed;
    },
    convertedMask() {
      const re = /0/gi;
      return this.mask.replace(re, '#');
    },
  },
  methods: {
    async getCities(val) {
      if (val.length < 3) {
        return;
      }
      const data = {
        scope: {
          search: val,
        },
      };
      try {
        const res = await request.post('/directory/region/', data);
        this.cities = res.data.data;
      } catch (err) {
        console.warn(err);
      }
    },
    async sendForm() {
      if (this.disableSend) return;
      if (!this.startSendingForm()) return;
      this.errors = {};
      this.form.data.email = this.form.data.email.replace(/\s+/g, '');
      let body = {...this.extraData};
      this.fields.forEach((field) => {
        body[field] = this.form.data[field];
      });

      if (body.phone) {
        body.phone = body.phone.replace(/[^\d]+/g, '').replace(/^8([^5]\d{9})$/, '7$1');
      }

      try {
        await requestBid.post(`/${this.formName}/`, {data: body});
        this.$emit('ok');
        this.$refs.form.resetFields();
        if (this.gtmEvent) {
          this.$gtm(this.gtmEvent);
        }
      } catch (err) {
        if ([400, 422, 429].includes(err.response.status)) {
          this.errors = err.response.data.errors;
        } else {
          alert(err.response.status + ' ' + err.response.statusText);
        }
      } finally {
        this.finishSendingForm();
      }
    },
  },
};
</script>
